import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { feedbackAPI } from '../../lib/api';
import { useRBAC } from '../../lib/RBACContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { MessageSquare, Send, Upload, X, CheckCircle, Clock, AlertCircle, Calendar, Image, Bug, Lightbulb, HelpCircle, AtSign, MessagesSquare, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MODULES = ['Dashboard', 'Dashboard Builder', 'Opportunities', 'Leads', 'Accounts', 'Activities', 'Invoices', 'Performance Hub', 'Analytics', 'Settings', 'General'];
const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-slate-100 text-slate-600' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-700' },
];
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-700', icon: Clock },
  explanation_sent: { label: 'Explanation Sent', color: 'bg-blue-100 text-blue-700', icon: MessageSquare },
  fix_planned: { label: 'Fix Planned', color: 'bg-violet-100 text-violet-700', icon: Calendar },
  fixed: { label: 'Fixed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-200 text-slate-700', icon: CheckCircle },
  in_review: { label: 'In Review', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: X },
  planned: { label: 'Planned', color: 'bg-purple-100 text-purple-700', icon: Calendar },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

function MentionText({ text = '' }) {
  const parts = text.split(/(@[\w.\-+@]+)/g);
  return <>{parts.map((part, i) => part.startsWith('@') ? <span key={i} className="font-semibold text-[#800000]">{part}</span> : <span key={i}>{part}</span>)}</>;
}

function FeedbackForm({ open, onClose }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [module, setModule] = useState('General');
  const [priority, setPriority] = useState('medium');
  const [screenshots, setScreenshots] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    setScreenshots(prev => [...prev, ...files].slice(0, 5));
  };
  const removeFile = (idx) => setScreenshots(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('module', module);
      formData.append('priority', priority);
      formData.append('page_url', window.location.pathname);
      screenshots.forEach(f => formData.append('screenshots', f));
      await feedbackAPI.submitFeedback(formData);
      toast.success('Feedback submitted!');
      setTitle(''); setDescription(''); setModule('General'); setPriority('medium'); setScreenshots([]);
      onClose();
    } catch { toast.error('Failed to submit feedback'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#800000]" /> New Communication</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-3">
            <button onClick={() => setPriority('low')} className={`flex-1 p-2 rounded-lg border text-center text-xs font-medium transition-all ${priority === 'low' ? 'border-[#800000] bg-[#800000]/5 text-[#800000]' : 'border-gray-200 text-gray-500'}`}><Lightbulb className="h-4 w-4 mx-auto mb-1" />Suggestion</button>
            <button onClick={() => setPriority('medium')} className={`flex-1 p-2 rounded-lg border text-center text-xs font-medium transition-all ${priority === 'medium' ? 'border-[#800000] bg-[#800000]/5 text-[#800000]' : 'border-gray-200 text-gray-500'}`}><HelpCircle className="h-4 w-4 mx-auto mb-1" />Request</button>
            <button onClick={() => setPriority('high')} className={`flex-1 p-2 rounded-lg border text-center text-xs font-medium transition-all ${priority === 'high' ? 'border-[#800000] bg-[#800000]/5 text-[#800000]' : 'border-gray-200 text-gray-500'}`}><Bug className="h-4 w-4 mx-auto mb-1" />Bug</button>
          </div>
          <div><Label className="text-xs text-gray-500">Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief summary" data-testid="feedback-title" /></div>
          <div><Label className="text-xs text-gray-500">Module</Label><Select value={module} onValueChange={setModule}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MODULES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-gray-500">Description</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe in detail... You can mention colleagues later in the reply thread with @name or @email." rows={4} data-testid="feedback-description" /></div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Screenshots (optional, max 5)</Label>
            <div className="flex flex-wrap gap-2">
              {screenshots.map((f, i) => <div key={i} className="relative group"><div className="w-16 h-16 rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden"><img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" /></div><button onClick={() => removeFile(i)} className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3 w-3" /></button></div>)}
              {screenshots.length < 5 && <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#800000] hover:bg-[#800000]/5 transition-colors"><Upload className="h-5 w-5 text-gray-400" /><input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" /></label>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-[#800000] hover:bg-[#9a1919] text-white" data-testid="feedback-submit"><Send className="h-4 w-4 mr-1" /> {submitting ? 'Sending...' : 'Submit Feedback'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="feedback-fab" className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-[#800000] text-white shadow-lg hover:bg-[#9a1919] hover:shadow-xl transition-all hover:scale-105" title="Open Communication Center">
        <MessageSquare className="h-5 w-5" />
      </button>
      <FeedbackForm open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function ThreadDialog({ item, open, onClose, isAdmin, onSaved }) {
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [reviewStatus, setReviewStatus] = useState(item?.status || 'pending');
  const [reviewNote, setReviewNote] = useState(item?.admin_note || '');
  const [savingReview, setSavingReview] = useState(false);

  const loadThread = useCallback(async () => {
    if (!item?.id) return;
    const res = await feedbackAPI.getFeedbackThread(item.id);
    setThread(res.data);
    setReviewStatus(res.data.item?.status || item.status || 'pending');
    setReviewNote(res.data.item?.admin_note || '');
  }, [item]);

  useEffect(() => { if (open && item?.id) loadThread(); }, [open, item, loadThread]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setReplying(true);
    try {
      await feedbackAPI.replyFeedback(item.id, { body: reply });
      toast.success('Reply posted');
      setReply('');
      await loadThread();
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post reply');
    } finally { setReplying(false); }
  };

  const saveReview = async () => {
    setSavingReview(true);
    try {
      await feedbackAPI.reviewFeedback(item.id, { status: reviewStatus, admin_note: reviewNote });
      toast.success('Feedback updated');
      await loadThread();
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feedback');
    } finally { setSavingReview(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="feedback-thread-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessagesSquare className="h-5 w-5 text-[#800000]" /> {item?.title}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6">
          <div className="space-y-4">
            <div className="rounded-2xl border bg-slate-50 p-4">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Badge className={`${(STATUS_CONFIG[thread?.item?.status] || STATUS_CONFIG.pending).color} text-[10px]`}>{(STATUS_CONFIG[thread?.item?.status] || STATUS_CONFIG.pending).label}</Badge>
                <Badge variant="outline" className="text-[10px]">{thread?.item?.module}</Badge>
                <Badge variant="outline" className="text-[10px]">{thread?.item?.priority}</Badge>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{thread?.item?.description}</p>
            </div>
            <div className="rounded-2xl border">
              <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800">Conversation</p>
                <p className="text-xs text-slate-500">Use @name or @email to notify people</p>
              </div>
              <ScrollArea className="h-[360px]">
                <div className="p-4 space-y-3">
                  {(thread?.messages || []).length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No conversation yet</p> : thread.messages.map((msg) => (
                    <div key={msg.id} className="rounded-2xl border bg-white px-4 py-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{msg.author?.name || msg.author?.email}</p>
                          <p className="text-[11px] text-slate-400">{new Date(msg.created_at).toLocaleString()}</p>
                        </div>
                        {msg.status_change && <Badge variant="outline" className="text-[10px]">{msg.status_change.replace(/_/g, ' ')}</Badge>}
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed"><MentionText text={msg.body} /></p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="border-t p-4 space-y-3">
                <Textarea value={reply} onChange={e => setReply(e.target.value)} rows={4} placeholder="Type a reply or explanation... Use @name or @email to notify someone." data-testid="feedback-reply-input" />
                <div className="flex justify-end">
                  <Button onClick={sendReply} disabled={replying || !reply.trim()} className="bg-[#800000] hover:bg-[#9a1919] text-white" data-testid="feedback-send-reply-btn"><Send className="h-4 w-4 mr-2" /> {replying ? 'Posting...' : 'Post Reply'}</Button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Card className="rounded-2xl border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Feedback Details</CardTitle>
                <CardDescription>People, status, and attachment context.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div><p className="text-xs text-slate-400 uppercase tracking-wider">Reporter</p><p className="font-medium text-slate-800">{thread?.item?.reporter?.name || thread?.item?.reporter?.email}</p></div>
                <div><p className="text-xs text-slate-400 uppercase tracking-wider">Page URL</p><p className="text-slate-600 break-all">{thread?.item?.page_url || '—'}</p></div>
                <div><p className="text-xs text-slate-400 uppercase tracking-wider">Shared With</p><div className="flex flex-wrap gap-1.5 mt-1">{(thread?.item?.shared_with || []).length === 0 ? <span className="text-slate-400">No mentions yet</span> : thread.item.shared_with.map(email => <Badge key={email} variant="outline" className="text-[10px]">@{email}</Badge>)}</div></div>
                {(thread?.item?.attachments || []).length > 0 && <div><p className="text-xs text-slate-400 uppercase tracking-wider">Attachments</p><div className="flex flex-wrap gap-2 mt-1">{thread.item.attachments.map(att => <a key={att.id} href={`/api/feedback/${thread.item.id}/attachments/${att.id}`} target="_blank" rel="noreferrer" className="text-xs text-[#800000] underline">{att.filename}</a>)}</div></div>}
              </CardContent>
            </Card>

            {isAdmin && (
              <Card className="rounded-2xl border-slate-200">
                <CardHeader>
                  <CardTitle className="text-base">Review & Explanation</CardTitle>
                  <CardDescription>Use this when a comment only needs an explanation or when a fix is planned/completed.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Status</Label>
                    <Select value={reviewStatus} onValueChange={setReviewStatus}>
                      <SelectTrigger data-testid="feedback-review-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Review / Explanation Note</Label>
                    <Textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)} rows={4} placeholder="Explain why this is expected, or note the fix plan/status." />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={saveReview} disabled={savingReview} className="bg-[#800000] hover:bg-[#9a1919] text-white" data-testid="feedback-save-review-btn">
                      {savingReview ? 'Saving...' : 'Save Status Update'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FeedbackAdminPage() {
  const [items, setItems] = useState([]);
  const [myItems, setMyItems] = useState([]);
  const [sharedItems, setSharedItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [activeItem, setActiveItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { permissions } = useRBAC();
  const isAdmin = permissions.includes('admin:*');
  const [searchParams, setSearchParams] = useSearchParams();

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleSelectAll = (list) => {
    const allIds = list.map(i => i.id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await feedbackAPI.bulkDeleteFeedback([...selectedIds]);
      toast.success(`Deleted ${selectedIds.size} item(s)`);
      setSelectedIds(new Set());
      setShowDeleteConfirm(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };
  const seenMap = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('feedback_notification_seen') || '{}'); } catch { return {}; }
  }, [notifications]);
  const unreadNotifications = useMemo(() => notifications.filter((note) => !seenMap[note.id] || new Date(note.created_at).getTime() > new Date(seenMap[note.id]).getTime()).length, [notifications, seenMap]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, sharedRes, adminRes, notifRes] = await Promise.allSettled([
        feedbackAPI.getMyFeedback(),
        feedbackAPI.getSharedFeedback(),
        isAdmin ? feedbackAPI.getAdminFeedback({ status: statusFilter || undefined, module: moduleFilter || undefined }) : Promise.resolve({ data: [] }),
        feedbackAPI.getNotifications({ limit: 100 }),
      ]);
      if (myRes.status === 'fulfilled') setMyItems(myRes.value.data || []);
      if (sharedRes.status === 'fulfilled') setSharedItems(sharedRes.value.data || []);
      if (adminRes.status === 'fulfilled') setItems(adminRes.value.data || []);
      if (notifRes.status === 'fulfilled') setNotifications(notifRes.value.data?.notifications || []);
    } catch { toast.error('Failed to load feedback'); }
    finally { setLoading(false); }
  }, [statusFilter, moduleFilter, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const feedbackId = searchParams.get('thread');
    if (!feedbackId) return;
    const target = [...items, ...myItems, ...sharedItems].find((entry) => entry.id === feedbackId);
    if (target) {
      setActiveItem(target);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, items, myItems, sharedItems, setSearchParams]);

  const FeedbackCard = ({ item, showActions = false, selectable = false }) => {
    const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
    const StatusIcon = sc.icon;
    const isChecked = selectedIds.has(item.id);
    return (
      <Card className={`hover:shadow-sm transition-shadow rounded-2xl ${isChecked ? 'ring-2 ring-[#800000]/30 bg-[#800000]/[0.02]' : ''}`} data-testid={`feedback-item-${item.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            {selectable && (
              <div className="pt-0.5 shrink-0">
                <input type="checkbox" checked={isChecked} onChange={() => toggleSelect(item.id)}
                  className="h-4 w-4 rounded border-gray-300 text-[#800000] focus:ring-[#800000] cursor-pointer" data-testid={`feedback-select-${item.id}`} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-semibold text-sm text-gray-900 truncate">{item.title}</span>
                <Badge className={`${sc.color} text-[10px]`}><StatusIcon className="h-3 w-3 mr-0.5" />{sc.label}</Badge>
                <Badge variant="outline" className="text-[10px]">{item.module}</Badge>
                <Badge variant="outline" className={`text-[10px] ${item.priority === 'high' ? 'border-red-200 text-red-600' : item.priority === 'medium' ? 'border-amber-200 text-amber-600' : ''}`}>{item.priority}</Badge>
              </div>
              <p className="text-xs text-gray-500 line-clamp-2">{item.description}</p>
              {item.last_message_preview && <p className="text-xs text-[#800000] mt-2 bg-[#800000]/5 rounded px-2 py-1">Latest: <MentionText text={item.last_message_preview} /></p>}
              <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400 flex-wrap">
                <span>{item.reporter?.name || item.reporter?.email}</span>
                <span>{new Date(item.created_at).toLocaleDateString()}</span>
                {item.attachments?.length > 0 && <span className="flex items-center gap-0.5"><Image className="h-3 w-3" />{item.attachments.length}</span>}
                {(item.shared_with || []).length > 0 && <span className="flex items-center gap-0.5"><AtSign className="h-3 w-3" />{item.shared_with.length}</span>}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setActiveItem(item)}>Open Thread</Button>
              {showActions && <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setActiveItem(item)}>Review</Button>}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const DeleteConfirmDialog = () => (
    <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
      <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="h-5 w-5" /> Delete {selectedIds.size} Item(s)?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">This will permanently delete the selected feedback items and all associated messages. This action cannot be undone.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
            <Trash2 className="h-4 w-4 mr-1" /> {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const BulkActionBar = ({ list }) => {
    if (selectedIds.size === 0) return null;
    const selectedInList = list.filter(i => selectedIds.has(i.id)).length;
    if (selectedInList === 0) return null;
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-[#800000]/5 border border-[#800000]/20" data-testid="bulk-action-bar">
        <span className="text-sm font-medium text-[#800000]">{selectedInList} selected</span>
        <Button size="sm" variant="outline" className="text-xs h-7 border-red-200 text-red-700 hover:bg-red-50" onClick={() => setShowDeleteConfirm(true)} data-testid="bulk-delete-btn">
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Selected
        </Button>
        <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setSelectedIds(new Set())}>Clear Selection</Button>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="feedback-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#800000]" /> Communication Center</h1>
        <p className="text-sm text-gray-500">Manage conversations, mentions, replies, and feedback updates in one place.</p>
      </div>

      <Tabs defaultValue={isAdmin ? 'admin' : 'my'}>
        <TabsList>
          <TabsTrigger value="notifications">Notifications {unreadNotifications > 0 ? `(${unreadNotifications} new)` : `(${notifications.length})`}</TabsTrigger>
          <TabsTrigger value="my">My Items ({myItems.length})</TabsTrigger>
          <TabsTrigger value="shared">Shared With Me ({sharedItems.length})</TabsTrigger>
          {isAdmin && <TabsTrigger value="admin">Feedback Queue ({items.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="notifications" className="mt-4 space-y-3">
          {loading ? <Card><CardContent className="p-8 text-sm text-slate-400">Loading notifications…</CardContent></Card> : notifications.length === 0 ? <div className="text-center py-12 text-gray-400"><Sparkles className="h-12 w-12 mx-auto mb-3 text-gray-200" /><p>No notifications yet</p></div> : notifications.map((note) => (
            <Card key={note.id} className="hover:shadow-sm transition-shadow rounded-2xl" data-testid={`feedback-notification-${note.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="text-[10px] bg-slate-100 text-slate-700">{STATUS_CONFIG[note.status]?.label || note.status}</Badge>
                    <Badge variant="outline" className="text-[10px]">{note.type?.replace('_', ' ')}</Badge>
                    <span className="text-sm font-semibold text-slate-800 truncate">{note.title}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2"><MentionText text={note.preview} /></p>
                  <p className="text-[10px] text-slate-400 mt-2">{note.author?.name || note.author?.email || 'System'} • {new Date(note.created_at).toLocaleString()}</p>
                </div>
                <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setActiveItem([...items, ...myItems, ...sharedItems].find((entry) => entry.id === note.feedback_id) || { id: note.feedback_id, title: note.title, status: note.status })}>Open</Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="my" className="mt-4 space-y-3">
          {!loading && myItems.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                <input type="checkbox" checked={myItems.length > 0 && myItems.every(i => selectedIds.has(i.id))} onChange={() => toggleSelectAll(myItems)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-[#800000] focus:ring-[#800000]" />
                Select All
              </label>
              <BulkActionBar list={myItems} />
            </div>
          )}
          {loading ? <Card><CardContent className="p-8 text-sm text-slate-400">Loading items…</CardContent></Card> : myItems.length === 0 ? <div className="text-center py-12 text-gray-400"><MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-200" /><p>No items submitted yet</p><p className="text-xs mt-1">Use the communication button (bottom-right) to send feedback or start a conversation.</p></div> : myItems.map(item => <FeedbackCard key={item.id} item={item} selectable />)}
        </TabsContent>

        <TabsContent value="shared" className="mt-4 space-y-3">
          {loading ? <Card><CardContent className="p-8 text-sm text-slate-400">Loading shared items…</CardContent></Card> : sharedItems.length === 0 ? <div className="text-center py-12 text-gray-400"><AtSign className="h-12 w-12 mx-auto mb-3 text-gray-200" /><p>No shared items yet</p><p className="text-xs mt-1">When someone mentions you with @name or @email, it will appear here.</p></div> : sharedItems.map(item => <FeedbackCard key={item.id} item={item} />)}
        </TabsContent>

        {isAdmin && <TabsContent value="admin" className="mt-4 space-y-4">
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={statusFilter || '_all'} onValueChange={v => setStatusFilter(v === '_all' ? '' : v)}>
              <SelectTrigger className="w-44 h-9 text-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent><SelectItem value="_all">All Status</SelectItem>{Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={moduleFilter || '_all'} onValueChange={v => setModuleFilter(v === '_all' ? '' : v)}>
              <SelectTrigger className="w-40 h-9 text-sm"><SelectValue placeholder="All Modules" /></SelectTrigger>
              <SelectContent><SelectItem value="_all">All Modules</SelectItem>{MODULES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex-1" />
            {items.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                <input type="checkbox" checked={items.length > 0 && items.every(i => selectedIds.has(i.id))} onChange={() => toggleSelectAll(items)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-[#800000] focus:ring-[#800000]" />
                Select All
              </label>
            )}
          </div>
          <BulkActionBar list={items} />
          {loading ? <Card><CardContent className="p-8 text-sm text-slate-400">Loading feedback queue…</CardContent></Card> : items.length === 0 ? <div className="text-center py-12 text-gray-400"><CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-200" /><p>No feedback to review</p></div> : items.map(item => <FeedbackCard key={item.id} item={item} showActions selectable />)}
        </TabsContent>}
      </Tabs>

      <DeleteConfirmDialog />
      <ThreadDialog item={activeItem} open={!!activeItem} onClose={() => setActiveItem(null)} isAdmin={isAdmin} onSaved={loadData} />
    </div>
  );
}
